// One mode's palette editor: a grid of color fields plus live WCAG feedback.
// Each field pairs a native color-picker swatch with a hex text input so
// admins can paste exact brand values. Feedback re-evaluates on every change.
// When the page's preview reports a clicked region (the `focus` prop), the
// matching fields flash and the first one scrolls into view.
import { useEffect, useState } from 'react';
import {
  Box, Typography, Stack, TextField, Alert, Button, Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import { evaluatePalette, isHexColor } from '../../../utils/wcag-contrast.js';

const TOKEN_FIELDS = [
  { key: 'bg', label: 'App background' },
  { key: 'surface', label: 'Card surface' },
  { key: 'surfaceRaised', label: 'Raised surface' },
  { key: 'text', label: 'Text' },
  { key: 'textDim', label: 'Secondary text' },
  { key: 'primary', label: 'Primary accent' },
  { key: 'secondary', label: 'Secondary accent' },
  { key: 'mapLand', label: 'Map land' },
  { key: 'mapLandStroke', label: 'Map county outline' },
  { key: 'mapHighlight', label: 'Map highlight' },
  { key: 'mapTopoGrid', label: 'Map tile grid' },
  { key: 'mapBackdrop', label: 'Map backdrop' },
];

const ColorField = ({
  label, value, onChange, flashing,
}) => {
  const valid = isHexColor(value);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: 1,
        transition: 'background-color 0.4s, box-shadow 0.4s',
        ...(flashing && {
          bgcolor: 'action.selected',
          boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}`,
        }),
      }}
    >
      <Box
        component="input"
        type="color"
        // The native picker needs a 6-digit hex; fall back to black on partial input.
        value={valid ? value : '#000000'}
        onChange={(e) => onChange(e.target.value)}
        aria-label={`${label} color picker`}
        sx={{
          width: 36,
          height: 36,
          p: 0,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'transparent',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      />
      <TextField
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        error={!valid}
        helperText={valid ? undefined : 'Hex color, e.g. #1d4ed8'}
        size="small"
        fullWidth
      />
    </Box>
  );
};

const PaletteModeEditor = ({
  title, palette, defaults, onChange, focus,
}) => {
  const results = evaluatePalette(palette);
  const failures = results.filter((r) => !r.pass);
  const isDefault = TOKEN_FIELDS.every(({ key }) => palette?.[key] === defaults?.[key]);

  const setToken = (key, value) => onChange({ ...palette, [key]: value });

  // Flash the fields the preview reported (no scroll — the highlight is
  // enough and scrolling was distracting). `focus.nonce` changes on every
  // preview click so repeat clicks on the same region re-flash.
  const [flashTokens, setFlashTokens] = useState([]);
  useEffect(() => {
    if (!focus?.tokens?.length) return undefined;
    setFlashTokens(focus.tokens);
    const timer = setTimeout(() => setFlashTokens([]), 1800);
    return () => clearTimeout(timer);
  }, [focus]);

  return (
    <Box sx={{ flex: 1, minWidth: 280 }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1,
      }}
      >
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, minWidth: 0,
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
          {failures.length === 0 ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              icon={<CheckCircleOutlineIcon />}
              label="WCAG"
            />
          ) : (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={failures.length === 1 ? '1 contrast issue' : `${failures.length} contrast issues`}
            />
          )}
        </Box>
        <Button
          size="small"
          onClick={() => onChange({ ...defaults })}
          disabled={isDefault}
        >
          Reset to defaults
        </Button>
      </Box>
      <Stack spacing={1.5}>
        {TOKEN_FIELDS.map(({ key, label }) => (
          <ColorField
            key={key}
            label={label}
            value={palette?.[key] ?? ''}
            onChange={(value) => setToken(key, value)}
            flashing={flashTokens.includes(key)}
          />
        ))}
      </Stack>

      <Box sx={{ mt: 2 }}>
        {failures.length === 0 ? (
          <Alert icon={<CheckCircleOutlineIcon fontSize="inherit" />} severity="success">
            All color combinations meet WCAG contrast guidelines.
          </Alert>
        ) : (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {failures.length === 1
                ? '1 combination is below the WCAG contrast bar:'
                : `${failures.length} combinations are below the WCAG contrast bar:`}
            </Typography>
            <Stack spacing={0.5}>
              {failures.map((f) => (
                <Box key={f.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    size="small"
                    label={f.ratio === null ? 'n/a' : `${f.ratio.toFixed(2)}:1`}
                    color="warning"
                    variant="outlined"
                  />
                  <Typography variant="body2">
                    {`${f.label} (needs ${f.min}:1)`}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default PaletteModeEditor;
